import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import Keycloak from 'keycloak-js';
import { LoggerService } from '../services';
import { environment } from '../../../environments/environment';

const CONTEXT = 'AuthGuard';
const LOGIN_ATTEMPTED_KEY = 'canvas-builder-login-attempted';

/**
 * Ensures user is authenticated via Keycloak.
 * Uses sessionStorage flag to prevent infinite redirect loops:
 * - First visit: if not authenticated, attempt login once
 * - After redirect back: if still not authenticated, allow access
 * - Flag is cleared on successful authentication
 */
export const authGuard: CanActivateFn = async () => {
  const keycloak = inject(Keycloak);
  const logger = inject(LoggerService);

  if (keycloak.authenticated) {
    // Clear the flag on successful auth
    sessionStorage.removeItem(LOGIN_ATTEMPTED_KEY);
    logger.debug('User authenticated', CONTEXT);
    return true;
  }

  // If login was already attempted and failed, allow access without auth
  if (sessionStorage.getItem(LOGIN_ATTEMPTED_KEY)) {
    sessionStorage.removeItem(LOGIN_ATTEMPTED_KEY);
    logger.warn('Login failed after redirect, allowing unauthenticated access', CONTEXT);
    return true;
  }

  // First attempt — try login once
  logger.info('Redirecting to login', CONTEXT);
  sessionStorage.setItem(LOGIN_ATTEMPTED_KEY, 'true');
  await keycloak.login({ idpHint: environment.keycloak.idpHint });
  return false;
};
